/**
 * DeveloperQuickstart — pitch-black 5-line code-drop section.
 *
 * Design blueprint section #8: "Pure tech, zero marketing fluff".
 * The hero claim of the entire page is "install in 5 lines".
 * This component delivers on it on the landing page itself —
 * no click-out to /developers required.
 *
 * Features per blueprint:
 *  - JetBrains Mono throughout
 *  - Emerald syntax highlighting on the base_url replacement
 *  - Copy-to-clipboard button with success toast
 *  - Easter egg: type `cartie --help` to trigger a modal
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Terminal, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const PYTHON_SNIPPET = [
  { tok: 'from', cls: 'text-rose-300' },
  { tok: ' openai ', cls: 'text-slate-200' },
  { tok: 'import', cls: 'text-rose-300' },
  { tok: ' OpenAI', cls: 'text-amber-200' },
  { tok: '\n\n', cls: '' },
  { tok: 'client', cls: 'text-slate-200' },
  { tok: ' = ', cls: 'text-slate-400' },
  { tok: 'OpenAI', cls: 'text-amber-200' },
  { tok: '(\n    ', cls: 'text-slate-400' },
  { tok: 'base_url', cls: 'text-sky-200' },
  { tok: '=', cls: 'text-slate-400' },
  { tok: '"https://api.cartieai.com/v1"', cls: 'text-emerald-300 bg-emerald-500/10 px-1 rounded-sm', highlight: true },
  { tok: ',\n    ', cls: 'text-slate-400' },
  { tok: 'api_key', cls: 'text-sky-200' },
  { tok: '=', cls: 'text-slate-400' },
  { tok: 'os', cls: 'text-amber-200' },
  { tok: '.', cls: 'text-slate-400' },
  { tok: 'environ', cls: 'text-slate-200' },
  { tok: '[', cls: 'text-slate-400' },
  { tok: '"CARTIE_KEY"', cls: 'text-emerald-200' },
  { tok: ']\n)\n\n', cls: 'text-slate-400' },
  { tok: '# Identical OpenAI API. We route, cache, compact, audit.\n', cls: 'text-slate-500 italic' },
  { tok: 'resp', cls: 'text-slate-200' },
  { tok: ' = ', cls: 'text-slate-400' },
  { tok: 'client', cls: 'text-slate-200' },
  { tok: '.', cls: 'text-slate-400' },
  { tok: 'chat', cls: 'text-slate-200' },
  { tok: '.', cls: 'text-slate-400' },
  { tok: 'completions', cls: 'text-slate-200' },
  { tok: '.', cls: 'text-slate-400' },
  { tok: 'create', cls: 'text-amber-200' },
  { tok: '(model=', cls: 'text-slate-400' },
  { tok: '"gpt-5.2"', cls: 'text-emerald-200' },
  { tok: ', messages=msgs)', cls: 'text-slate-400' },
];

const RAW_TEXT = `from openai import OpenAI

client = OpenAI(
    base_url="https://api.cartieai.com/v1",
    api_key=os.environ["CARTIE_KEY"]
)

# Identical OpenAI API. We route, cache, compact, audit.
resp = client.chat.completions.create(model="gpt-5.2", messages=msgs)`;

const TABS = [
  { id: 'python',     label: 'python',     file: 'main.py' },
  { id: 'typescript', label: 'typescript', file: 'index.ts' },
  { id: 'curl',       label: 'curl',       file: 'shell.sh' },
];

const TS_SNIPPET = `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "https://api.cartieai.com/v1",   // ← one line
  apiKey:  process.env.CARTIE_KEY,
});

// Identical OpenAI SDK. We route, cache, compact, audit.
const r = await client.chat.completions.create({ model: "gpt-5.2", messages });`;

const CURL_SNIPPET = `curl https://api.cartieai.com/v1/chat/completions \\
  -H "Authorization: Bearer $CARTIE_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "model": "gpt-5.2", "messages": [{"role":"user","content":"hi"}] }'`;

export default function DeveloperQuickstart() {
  const [tab, setTab] = useState('python');
  const [copied, setCopied] = useState(false);
  const [easterCmd, setEasterCmd] = useState('');
  const [easterOpen, setEasterOpen] = useState(false);
  const inputRef = useRef(null);

  const snippet = tab === 'python' ? RAW_TEXT : tab === 'typescript' ? TS_SNIPPET : CURL_SNIPPET;

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    toast.success('Copied. Now ship it.', { duration: 2000 });
    setTimeout(() => setCopied(false), 2000);
  };

  // Easter egg — typing `cartie --help` in the input triggers a help modal.
  useEffect(() => {
    if (easterCmd.trim().toLowerCase() === 'cartie --help') {
      setEasterOpen(true);
      setEasterCmd('');
    }
  }, [easterCmd]);

  return (
    <section
      id="quickstart"
      className="relative py-24 px-4 sm:px-6 border-t border-white/5 scroll-mt-24 overflow-hidden"
      data-testid="landing-quickstart"
    >
      {/* v34.62 — soft emerald radial glow to add depth to the otherwise-flat
          code-drop section. Pure CSS, no animation overhead. */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute left-0 top-0 w-[500px] h-[500px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />
        <div className="absolute right-0 bottom-0 w-[600px] h-[400px] rounded-full bg-teal-500/[0.05] blur-[120px]" />
      </div>
      <div className="max-w-6xl mx-auto relative">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-start">
          {/* Left — copy column */}
          <div className="lg:sticky lg:top-28">
            <p className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-300/80 mb-5">
              <Terminal className="w-3.5 h-3.5" />
              <span>$ developer quickstart</span>
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.035em] leading-[0.95] text-white">
              Install in<br />
              <span className="text-emerald-400">five lines.</span>
            </h2>
            <p className="mt-7 text-base sm:text-lg text-slate-400 leading-relaxed max-w-md">
              Drop-in OpenAI replacement. Identical SDK, identical response shape.
              Change one URL, get token observability, semantic cache, prompt compaction, and
              cross-cloud arbitrage — for free, on the way to the model.
            </p>

            {/* Inline metrics — typographic, no boxes */}
            <div className="mt-9 grid grid-cols-2 gap-y-5 max-w-md">
              {[
                { num: '5',   sub: 'lines of code' },
                { num: '0ms', sub: 'added p50 latency' },
                { num: '11',  sub: 'LLM providers routed' },
                { num: '$0',  sub: 'on cache hits' },
              ].map((k) => (
                <div key={k.sub}>
                  <div className="font-display text-3xl sm:text-4xl font-black text-emerald-300 tabular-nums tracking-tight">
                    {k.num}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-1">
                    {k.sub}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/developers/quickstart" data-testid="quickstart-deeplink">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline">
                  Read the full quickstart <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
              <span className="text-slate-700">·</span>
              <Link to="/sdk" data-testid="quickstart-sdk-link">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-white">
                  SDK reference
                </span>
              </Link>
            </div>
          </div>

          {/* Right — code editor */}
          <div className="relative">
            {/* Editor chrome — matched to landing card aesthetic (slate-900 + rounded + emerald border tint) */}
            <div className="relative bg-slate-900 border border-emerald-500/20 rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/5">
              {/* Title bar */}
              <div className="flex items-center justify-between border-b border-white/5 bg-slate-950/60">
                <div className="flex items-center gap-1.5 px-4 py-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                </div>
                <div className="flex">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`px-4 py-2.5 text-[11px] font-mono border-l border-white/5 transition-all ${
                        tab === t.id
                          ? 'text-emerald-300 bg-slate-900 border-b border-b-emerald-400/40'
                          : 'text-slate-500 hover:text-slate-300'
                      }`}
                      data-testid={`quickstart-tab-${t.id}`}
                    >
                      {t.file}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleCopy}
                  className="px-4 py-2.5 text-[11px] font-mono text-slate-400 hover:text-emerald-300 border-l border-white/5 transition-colors inline-flex items-center gap-1.5"
                  data-testid="quickstart-copy-btn"
                >
                  {copied ? (
                    <><Check className="w-3.5 h-3.5 text-emerald-400" /> copied</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5" /> copy</>
                  )}
                </button>
              </div>

              {/* Code body */}
              <div className="grid grid-cols-[auto_1fr] font-mono text-[13px] sm:text-[14px] leading-[1.7]">
                {/* Line numbers */}
                <div className="px-4 py-6 border-r border-white/5 text-right select-none">
                  {snippet.split('\n').map((_, i) => (
                    <div key={i} className="text-slate-700 tabular-nums">{i + 1}</div>
                  ))}
                </div>

                {/* Code */}
                <pre className="p-6 overflow-x-auto" data-testid="quickstart-code-body">
                  {tab === 'python' ? (
                    <code>
                      {PYTHON_SNIPPET.map((t, i) => (
                        <span key={i} className={t.cls}>{t.tok}</span>
                      ))}
                    </code>
                  ) : (
                    <code className="text-slate-200 whitespace-pre">{snippet}</code>
                  )}
                </pre>
              </div>

              {/* Easter-egg footer input */}
              <div className="border-t border-white/5 px-4 py-2.5 bg-slate-950/60 flex items-center gap-2 font-mono text-[12px]">
                <span className="text-emerald-400">$</span>
                <input
                  ref={inputRef}
                  value={easterCmd}
                  onChange={(e) => setEasterCmd(e.target.value)}
                  placeholder="try: cartie --help"
                  className="flex-1 bg-transparent outline-none text-slate-300 placeholder:text-slate-600"
                  data-testid="quickstart-easter-input"
                />
                <span className="w-2 h-4 bg-emerald-400 animate-pulse" />
              </div>
            </div>

            {/* Below-editor breakdown — what each line does */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 font-mono text-[11px] text-slate-500">
              <div className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">▸</span>
                <span><span className="text-emerald-300">base_url</span> = where every request flows</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">▸</span>
                <span><span className="text-emerald-300">api_key</span> = your tenant + budget envelope</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">▸</span>
                <span>everything else = your existing code</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Easter-egg modal */}
      {easterOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          onClick={() => setEasterOpen(false)}
          data-testid="quickstart-easter-modal"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div
            className="relative max-w-2xl w-full bg-slate-900 border border-emerald-500/40 rounded-2xl font-mono text-[13px] p-8 shadow-2xl shadow-emerald-500/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-emerald-400 mb-4">
              $ cartie --help
            </div>
            <div className="text-slate-300 space-y-2.5 leading-relaxed">
              <div><span className="text-emerald-300">Usage:</span> cartie [COMMAND]</div>
              <div className="pl-4 space-y-1.5">
                <div><span className="text-amber-300">why</span>          explain a cost spike with an LLM</div>
                <div><span className="text-amber-300">cache</span>        probe the semantic cache</div>
                <div><span className="text-amber-300">compact</span>      run the prompt compactor</div>
                <div><span className="text-amber-300">simulate</span>     decision simulator (what-if)</div>
                <div><span className="text-amber-300">audit</span>        shadow AI seat audit</div>
                <div><span className="text-amber-300">verify</span>       contractually-binding ROI math</div>
              </div>
              <div className="pt-4 text-slate-500">
                You found the easter egg. Email <a href="mailto:hello@cartieai.com" className="text-emerald-300 underline-offset-4 hover:underline">hello@cartieai.com</a> with "easter egg" — we'll send you a CARTIE t-shirt.
              </div>
            </div>
            <button
              onClick={() => setEasterOpen(false)}
              className="absolute top-3 right-3 text-slate-500 hover:text-white text-xs font-mono"
              data-testid="quickstart-easter-close"
            >
              [ esc ]
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
